import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { ProjectManagementService } from '../project-management.service';

@Component({
  selector: 'app-project-onboarding',
  templateUrl: './project-onboarding.component.html',
  styleUrls: ['./project-onboarding.component.scss']
})
export class ProjectOnboardingComponent implements OnInit {
  loading = false;
  saving = false;
  projects: any[] = [];
  rows: any[] = [];
  selectedProjectId: number | null = null;
  selectedItem: any = null;
  acknowledge = false;

  form: any = {
    title: '',
    description: '',
    attachmentUrl: '',
    attachmentName: '',
    status: 'Submitted'
  };

  constructor(private projectService: ProjectManagementService, private auth: AuthService) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  loadProjects(): void {
    this.loading = true;
    this.projectService.getProjects().subscribe({
      next: (projects) => {
        this.projects = projects || [];
        this.selectedProjectId = this.projects[0]?.id || null;
        this.loading = false;
        this.loadOnboarding();
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load projects.', 'error');
      }
    });
  }

  loadOnboarding(): void {
    if (!this.selectedProjectId) {
      this.rows = [];
      return;
    }

    this.loading = true;
    this.projectService.getProjectOnboarding(this.selectedProjectId).subscribe({
      next: (rows) => {
        this.rows = rows || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load onboarding items.', 'error');
      }
    });
  }

  editItem(row: any): void {
    this.selectedItem = row;
    this.form = {
      title: row?.title || '',
      description: row?.details || '',
      attachmentUrl: row?.attachmentUrl || '',
      attachmentName: row?.attachmentName || '',
      status: row?.status || 'Submitted'
    };
    this.acknowledge = false;
  }

  resetForm(): void {
    this.selectedItem = null;
    this.form = {
      title: '',
      description: '',
      attachmentUrl: '',
      attachmentName: '',
      status: 'Submitted'
    };
    this.acknowledge = false;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      Swal.fire('Invalid file', 'Please upload a PDF document.', 'warning');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.form.attachmentUrl = reader.result as string;
      this.form.attachmentName = file.name;
      this.acknowledge = false;
    };
    reader.readAsDataURL(file);
  }

  save(): void {
    if (!this.selectedProjectId) {
      Swal.fire('Validation', 'Please choose a project first.', 'warning');
      return;
    }

    if (!this.form.title?.trim()) {
      Swal.fire('Validation', 'Title is required.', 'warning');
      return;
    }

    if (!this.form.attachmentUrl) {
      Swal.fire('Validation', 'Please upload a PDF requirement document.', 'warning');
      return;
    }

    if (!this.acknowledge) {
      Swal.fire('Acknowledge', 'Confirm this is the correct requirement document before saving.', 'info');
      return;
    }

    if (!this.canManageOnboarding()) return;

    const payload = {
      title: this.form.title?.trim(),
      details: this.form.description || null,
      attachmentUrl: this.form.attachmentUrl,
      attachmentName: this.form.attachmentName || 'Requirement.pdf',
      status: this.form.status || 'Submitted'
    };

    this.saving = true;
    const request$ = this.selectedItem
      ? this.projectService.updateOnboardingItem(this.selectedItem.id, payload)
      : this.projectService.createOnboardingItem(this.selectedProjectId, payload);

    request$.subscribe({
      next: () => {
        this.saving = false;
        Swal.fire('Saved', 'Requirement recorded. To change it later, upload a new PDF and mark it as current.', 'success');
        this.resetForm();
        this.loadOnboarding();
      },
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to save onboarding item.', 'error');
      }
    });
  }

  canManageOnboarding(): boolean {
    return this.auth.isAdmin()
      || this.auth.hasPermission('project.onboarding.manage')
      || this.auth.hasPermission('project.client.onboarding')
      || (this.auth.getRole() || '').toLowerCase() === 'client';
  }
}
